/* eslint-disable max-lines */

import ElevationProfile from '@gaia/components/ElevationProfile';
import CopyLinkButton from '@gaia/components/hike-ui/CopyLinkButton';
import ExpandableText, {LastEditedByProps} from '@gaia/components/hike-ui/ExpandableText';
import FieldEditButton from '@gaia/components/hike-ui/FieldEditButton';
import FlagButton from '@gaia/components/hike-ui/FlagButton';
import MergeOverlapButton from '@gaia/components/hike-ui/MergeOverlapButton';
import PhotoLightBox from '@gaia/components/hike-ui/PhotoLightBox';
import PhotoMapWall from '@gaia/components/hike-ui/PhotoMapWall';
import ReviewForm, {Review} from '@gaia/components/hike-ui/ReviewForm';
import SaveRegionButton, {LocalizableStrings} from '@gaia/components/hike-ui/SaveRegionButton';
import SortOptions, {
    ActivityId,
    SortDirection,
    SortProperty,
} from '@gaia/components/hike-ui/SortOptions';
import startReview from '@gaia/components/hike-ui/startReview';
import MonthlyTrendProfile from '@gaia/components/MonthlyTrendProfile';
import SearchBox from '@gaia/components/SearchBox';
import theme from '@gaia/theme';
import {RegionType, type RegionTypeDiscriminator} from '@gaia/types';
import downloadGeoFile from '@gaia/utils/downloadGeoFile';
import getDirections from '@gaia/utils/gaia/hike-ui/getDirections';
import onPageChange from '@gaia/utils/gaia/hike-ui/onPageChange';
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import turfAlong from '@turf/along';
import type {Feature, LineString, Point, Properties} from '@turf/helpers';
import {LngLat, MapMouseEvent} from 'mapbox-gl';
import Pagination from 'rc-pagination';
import 'rc-pagination/assets/index.css';
import en_US from 'rc-pagination/es/locale/en_US';
import ReactDOM from 'react-dom';
import Rater from 'react-rater';

import 'react-rater/lib/react-rater.css';

declare global {
    interface Window {
        HikeUI: {
            initialize: (options: InitializeOptions) => void;
            renderPhotoMapWall: (
                options: any,
                mapMarker?: any,
                highlightId?: any,
                highlightPoint?: any,
                hightlightType?: any,
                highlightIndex?: any
            ) => void;
            renderSearchBox: (center: LngLat | undefined, selector: string) => void;
            startReview: (options: any) => void;
        };

        hikeAppRoot: {
            downloadFile: (
                fileDownloadUrl: string,
                permalink: string,
                cb: (error: string | null) => void
            ) => void;
            getDirections: (lat: number, lng: number) => void;
            updateQueryString: (
                key: string,
                value: string | null | undefined,
                url: string
            ) => string;
        };

        mapMarker: Feature<Point, Properties>;
    }
}

interface InitializeOptions {
    activity: ActivityId;
    elevationProfile: [number, number, number, number][]; // time, distance, lon, lat
    expandDescriptionInitially: boolean;
    flagged: boolean;
    flaggedNote: string;
    geometry: LineString; // GeoJSON probably
    hasActiveSubscription: boolean;
    hasApp: boolean;
    initialStaticDescription: KnownRouteDescriptionSection[];
    localizableStrings: LocalizableStrings;
    maxDistance: string;
    originalDescription: KnownRouteDescriptionSection[];
    originalTitle: string;
    pageDict: {
        order_by: SortDirection;
        sort: SortProperty;
        page: number;
        page_size: number;
        total_results: number;
    };
    regionId: `${number}`;
    regionType: RegionTypeDiscriminator;
    reviews: Review[];
    saved: boolean;
    seasonProfile: unknown;
    selectors: {
        base: string;
        copyLinkButton: string;
        editButtonTitle: string;
        elevationGraph: string;
        expandableDescription: string;
        flagButton: string;
        lightboxContainer: string;
        overlapCheckerTitle: string;
        pagination: string;
        photoWall: string;
        reviewForm: string;
        saveButton: string;
        search: string;
        seasonGraph: string;
        sortOptions: string;
        stars: string;
    };
    starCount: number;
    useMetric: boolean;
    user: {id: string};
    userGeneratedDescription: any;
    userGeneratedUser: LastEditedByProps;
    userGeneratedName: string;
    centroid: unknown;
    description: unknown;
    extent: unknown;
    isHikePage: true;
    isStaff: boolean;
    minDistance: number;
    photoCount: number;
    photos: unknown;

    // these aren't passed in base_trip.html
    mapCenter?: LngLat;
    onRate?: (event: any) => void;
}

window.HikeUI = {
    // add a bunch of react components to the DOM
    initialize(options) {
        const regionId = parseInt(options.regionId, 10);

        if (window.document.querySelector(options.selectors.sortOptions)) {
            ReactDOM.render(
                <SortOptions
                    orderBy={options.pageDict.order_by}
                    sort={options.pageDict.sort}
                    // minDistance={options.minDistance} // prop does not exist on SortOptions
                    maxDistance={options.maxDistance}
                    activity={options.activity}
                    useMetric={options.useMetric}
                />,
                window.document.querySelector(options.selectors.sortOptions)
            );
        }

        if (window.document.querySelector(options.selectors.pagination)) {
            ReactDOM.render(
                <Pagination
                    onChange={onPageChange}
                    className='ant-pagination'
                    current={options.pageDict.page}
                    total={options.pageDict.total_results}
                    pageSize={options.pageDict.page_size}
                    locale={en_US}
                />,
                window.document.querySelector(options.selectors.pagination)
            );
        }

        window.HikeUI.renderPhotoMapWall(options);

        if (
            options.regionType === RegionType.KNOWN_ROUTE ||
            options.regionType === RegionType.LAND
        ) {
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <SaveRegionButton
                            hasActiveSubscription={options.hasActiveSubscription}
                            hasApp={options.hasApp}
                            localizableStrings={options.localizableStrings}
                            regionId={regionId}
                            regionType={options.regionType}
                            saved={options.saved}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.saveButton)
            );
        }

        if (window.document.querySelector(options.selectors.flagButton)) {
            // the flag button in the toolbar below the photos/map
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <FlagButton
                            flagged={options.flagged}
                            flaggedNote={options.flaggedNote}
                            regionId={regionId}
                            regionType={options.regionType}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.flagButton)
            );
        }

        if (window.document.querySelector(options.selectors.copyLinkButton)) {
            // the copy link button in the toolbar below the photos/map
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <CopyLinkButton />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.copyLinkButton)
            );
        }

        if (window.document.querySelector(options.selectors.editButtonTitle)) {
            let startingEditTitle = options.originalTitle;
            if (options.userGeneratedName) {
                startingEditTitle = options.userGeneratedName;
            }

            // the edit button next to the title
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <FieldEditButton
                            // these props were missing and are req'd by FieldEditButton
                            open={false} // assume undefined (falsy) => false
                            onRequestClose={() => {}}
                            text={''}
                            title={''}
                            afterUpdate={(title) => {
                                // use DOM because we want the title originally rendered statically for SEO
                                const titleSpan = document.getElementById('titleSpan')!;
                                titleSpan.innerHTML = title;
                                const breadcrumbsDiv = document.getElementById('breadcrumbs-name')!;
                                breadcrumbsDiv.innerHTML = title;
                            }}
                            anchorTitle={'edit'}
                            apiEndpoint={'update'}
                            apiType={'title'}
                            editedText={startingEditTitle}
                            editTitle={'Edit Title'}
                            regionId={regionId}
                            regionType={options.regionType}
                            originalText={options.originalTitle}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.editButtonTitle)
            );
        }

        if (window.document.querySelector(options.selectors.overlapCheckerTitle)) {
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <MergeOverlapButton
                            regionType={options.regionType}
                            regionId={options.regionId}
                            title={'Merge into routes'}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.overlapCheckerTitle)
            );
        }

        if (window.document.querySelector(options.selectors.expandableDescription)) {
            const hasUserGeneratedDescriptionText =
                options.userGeneratedDescription &&
                options.userGeneratedDescription.length > 1 &&
                options.userGeneratedDescription[1].text &&
                options.userGeneratedDescription[1].text.length > 0;
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <ExpandableText
                            // these props do not exist on ExpandableText:
                            // anchorTitle={'Edit'}
                            // apiEndpoint={'update'}
                            // editedText={
                            //     options.description == options.originalText
                            //         ? null
                            //         : options.description
                            // }
                            // editTitle={'Edit Description'}

                            // these props were missing and req'd by ExpandableText
                            description={[]}
                            expandDescriptionInitially={options.expandDescriptionInitially}
                            initialStaticDescription={options.initialStaticDescription}
                            lastEditedBy={options.userGeneratedUser}
                            regionId={regionId}
                            regionType={options.regionType}
                            originalText={options.originalDescription}
                            showExpandButton={
                                hasUserGeneratedDescriptionText &&
                                !options.expandDescriptionInitially
                            }
                            text={options.initialStaticDescription}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.expandableDescription)
            );
        }

        // show a star rating if this object is rated
        if (options.starCount) {
            // https://github.com/NdYAG/react-rater
            ReactDOM.render(
                <Rater
                    total={5}
                    rating={options.starCount}
                    interactive={false}
                    onRate={options.onRate}
                />,
                window.document.querySelector(options.selectors.stars)
            );
        }

        // show an elevation graph if elevation data exists for the object
        if (options.elevationProfile) {
            ReactDOM.render(
                <ElevationProfile
                    elevation={options.elevationProfile}
                    onMouseover={(event: any) => {
                        const mapMarker = turfAlong(
                            {
                                type: 'Feature',
                                geometry: options.geometry,
                                properties: {},
                            },
                            event.distance / 1000,
                            {units: 'kilometers'}
                        );
                        window.mapMarker = mapMarker;
                        window.HikeUI.renderPhotoMapWall(options, mapMarker);
                    }}
                    onMouseout={() => window.HikeUI.renderPhotoMapWall(options, null)}
                />,
                window.document.querySelector(options.selectors.elevationGraph)
            );
        }

        // show monthly visits histogram if data exists for the object
        if (options.seasonProfile) {
            ReactDOM.render(
                <MonthlyTrendProfile
                    // @ts-ignore
                    months={options.seasonProfile}
                    yAxisLabel='Visits'
                />,
                window.document.querySelector(options.selectors.seasonGraph)
            );
        }

        // show a review form for hikes
        if (options.regionType === RegionType.KNOWN_ROUTE) {
            ReactDOM.render(
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <ReviewForm
                            // these props were missing and are req'd by FieldEditButton
                            initialRating={0}
                            initialNotes={''}
                            userId={options.user.id}
                            reviews={options.reviews}
                            regionType={options.regionType}
                            regionId={regionId}
                        />
                    </ThemeProvider>
                </StyledEngineProvider>,
                window.document.querySelector(options.selectors.reviewForm)
            );
        }

        window.HikeUI.renderSearchBox(options.mapCenter, options.selectors.search);
    },

    // eslint-disable-next-line max-params
    renderPhotoMapWall(
        options,
        mapMarker,
        highlightId,
        highlightPoint,
        highlightType,
        highlightIndex
    ) {
        // are we sure that ReactDOM.render() returns an element?

        const photoLightboxCom = ReactDOM.render(
            <PhotoLightBox
                photos={options.photos}
                isVisible={false}
                title={options.title}
                isStaff={options.isStaff}
                // user={options.user} // prop does not exist on PhotoLightBox???
                regionType={options.regionType}
                regionId={options.regionId}
            />,
            window.document.querySelector(options.selectors.lightboxContainer)
        ) as unknown as PhotoLightBox;

        function switchContainers() {
            ReactDOM.render(<div />, window.document.querySelector(options.selectors.photoWall));
            if (options.selectors.photoWall === '#photomapwall-container') {
                options.selectors.photoWall = '#big-photomapwall-container';
            } else {
                options.selectors.photoWall = '#photomapwall-container';
            }
            renderPhotoWallForOptions(options);
        }

        function renderPhotoWallForOptions(photoWallOptions: any) {
            ReactDOM.render(
                <PhotoMapWall
                    centroid={photoWallOptions.centroid}
                    cover_photo_id={photoWallOptions.cover_photo_id}
                    extent={photoWallOptions.extent}
                    geometry={photoWallOptions.geometry}
                    highlightId={highlightId}
                    highlightIndex={highlightIndex}
                    highlightPoint={highlightPoint}
                    highlightType={highlightType}
                    isMapExpanded={options.selectors.photoWall == '#big-photomapwall-container'}
                    mapMarker={mapMarker}
                    photos={photoWallOptions.photos}
                    photoCount={photoWallOptions.photoCount}
                    photoLightboxCom={photoLightboxCom}
                    regionId={options.regionId}
                    regionType={options.regionType}
                    reviews={options.reviews}
                    startPoints={options.pageDict.route_start_points}
                    startPointsExtent={options.pageDict.start_points_extent}
                    switchContainersMethod={
                        options.regionType != RegionType.KNOWN_ROUTE
                            ? () => switchContainers()
                            : () => {}
                    }
                    title={photoWallOptions.title}
                    onMapMove={(event: MapMouseEvent) => {
                        const center = event.target.getCenter().wrap();
                        window.HikeUI.renderSearchBox(
                            new LngLat(center.lng, center.lat),
                            options.selectors.search
                        );
                    }}
                    user={options.user}
                />,
                window.document.querySelector(photoWallOptions.selectors.photoWall)
            );
        }

        renderPhotoWallForOptions(options);
    },

    // the top component with photos and a map
    renderSearchBox(center, selector) {
        ReactDOM.render(
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <SearchBox
                        center={center}
                        resultsContainerStyle={{
                            ...(window.innerWidth > 768 && {right: 0}),
                        }}
                    />
                </ThemeProvider>
            </StyledEngineProvider>,
            document.querySelector(selector)
        );
    },

    // Called when a a user is directed to a hike page by a drip email.
    startReview,
};

window.hikeAppRoot = {
    downloadFile(fileDownloadUrl, permalink, cb) {
        downloadGeoFile(fileDownloadUrl, permalink, cb);
    },
    getDirections,
    updateQueryString(key, value, url) {
        const re = new RegExp('([?&])' + key + '=.*?(&|#|$)(.*)', 'gi');
        let hash;

        if (re.test(url)) {
            if (typeof value !== 'undefined' && value !== null) {
                return url.replace(re, '$1' + key + '=' + value + '$2$3');
            } else {
                hash = url.split('#');
                url = hash[0].replace(re, '$1$3').replace(/(&|\?)$/, '');
                if (typeof hash[1] !== 'undefined' && hash[1] !== null) {
                    url += '#' + hash[1];
                }
                return url;
            }
        } else if (typeof value !== 'undefined' && value !== null) {
            const separator = url.indexOf('?') !== -1 ? '&' : '?';
            hash = url.split('#');
            url = hash[0] + separator + key + '=' + value;
            if (typeof hash[1] !== 'undefined' && hash[1] !== null) {
                url += '#' + hash[1];
            }
            return url;
        } else {
            return url;
        }
    },
};
